import { useRouter } from "@/router/hooks";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import type { KeepAliveTab } from "../types";

const HOMEPAGE = import.meta.env.VITE_APP_HOMEPAGE || "/dashboard/home";

export function useTabOperations(
	tabs: KeepAliveTab[],
	setTabs: Dispatch<SetStateAction<KeepAliveTab[]>>,
	activeTabRoutePath: string,
) {
	const { push } = useRouter();

	const closeTab = useCallback(
		(path = activeTabRoutePath) => {
			const tempTabs = [...tabs];
			if (tempTabs.length === 1) return;

			const deleteTabIndex = tempTabs.findIndex((item) => item.key === path);
			if (deleteTabIndex === -1) return;

			if (deleteTabIndex > 0) {
				push(tempTabs[deleteTabIndex - 1].key);
			} else {
				push(tempTabs[deleteTabIndex + 1].key);
			}

			tempTabs.splice(deleteTabIndex, 1);
			setTabs(tempTabs);
		},
		[activeTabRoutePath, push, tabs, setTabs],
	);

	const closeOthersTab = useCallback(
		(path = activeTabRoutePath) => {
			setTabs((prev) => prev.filter((item) => item.key === path));
			if (path !== activeTabRoutePath) {
				push(path);
			}
		},
		[activeTabRoutePath, push, setTabs],
	);

	const closeAll = useCallback(() => {
		setTabs([]);
		push(HOMEPAGE);
	}, [push, setTabs]);

	const closeLeft = useCallback(
		(path: string) => {
			const currentTabIndex = tabs.findIndex((item) => item.key === path);
			const newTabs = tabs.slice(currentTabIndex);
			setTabs(newTabs);
			push(path);
		},
		[push, tabs, setTabs],
	);

	const closeRight = useCallback(
		(path: string) => {
			const currentTabIndex = tabs.findIndex((item) => item.key === path);
			const newTabs = tabs.slice(0, currentTabIndex + 1);
			setTabs(newTabs);
			push(path);
		},
		[push, tabs, setTabs],
	);

	const refreshTab = useCallback(
		(path = activeTabRoutePath) => {
			setTabs((prev) => {
				const newTabs = [...prev];
				const index = newTabs.findIndex((item) => item.key === path);
				if (index >= 0) {
					newTabs[index] = {
						...newTabs[index],
						timeStamp: Date.now().toString(),
					};
				}
				return newTabs;
			});
		},
		[activeTabRoutePath, setTabs],
	);

	const closeDashboardTab = useCallback(
		(dashboardId: string | number) => {
			const targetIdStr = String(dashboardId);
			setTabs((prevTabs) => {
				return prevTabs.filter((tab) => {
					if (tab.params) {
						if (tab.params.dashboard_id && String(tab.params.dashboard_id) === targetIdStr) return false;
						if (tab.params.id && String(tab.params.id) === targetIdStr) return false;
					}
					if (tab.key) {
						const cleanPath = tab.key.split("?")[0];
						const segments = cleanPath.split("/").filter(Boolean);
						if (segments[0] === "dashboard" && segments[1] === targetIdStr) return false;
					}
					return true;
				});
			});
		},
		[setTabs],
	);

	return {
		closeTab,
		closeDashboardTab,
		closeOthersTab,
		closeAll,
		closeLeft,
		closeRight,
		refreshTab,
	};
}
